/**
 * Quiz Buzz — host-side state machine + scoring.
 *
 * The host runs a fixed number of rounds. Each round picks an unused
 * question, shuffles its option order, and broadcasts to phones a
 * spec where A/B/X/Y are coloured option buttons. Players pick on
 * their phone; the host locks the first press per player and scores
 * after the timer (or once everyone's in).
 *
 * Scoring: correct = 100 base + linear speed bonus that caps at 100,
 * decaying over the question window. Wrong / no answer = 0. Speed
 * keeps fast experts engaged without crushing slower players.
 */

import { QUESTIONS, type Question } from './questions.js';

export const MAX_PLAYERS = 8;
export const ROUNDS_PER_GAME = 10;
export const QUESTION_TIME_S = 18;
export const PRE_ROUND_S = 2.6;
export const REVEAL_S = 4.5;
export const FINAL_HOLD_S = 12;
export const SCORE_BASE = 100;
export const SCORE_SPEED_MAX = 100;

export const OPTION_COLOURS = ['#ff8a3a', '#58ff58', '#b48cff', '#ffd84a'];
export const OPTION_GLYPHS = ['A', 'B', 'X', 'Y'];
export const OPTION_SLOTS = ['A', 'B', 'X', 'Y'] as const;
export type OptionSlot = typeof OPTION_SLOTS[number];

export function isAnswerPress(slot: string, value: string): slot is OptionSlot {
  return value === '1' && OPTION_SLOTS.includes(slot as OptionSlot);
}

export interface Player {
  p: number;
  colour: string;
  score: number;
  /** Index into the shuffled options for the current question, null
   *  if not picked yet, frozen on reveal. */
  pick: 0 | 1 | 2 | 3 | null;
  /** Time elapsed (s into the question) at the moment of pick. */
  pickedAt: number | null;
  /** Whether the most recently scored round was correct (for reveal). */
  lastCorrect: boolean;
  /** Points won in the most recent round (for reveal animation). */
  lastDelta: number;
  /** True for host-side bots filling otherwise-empty slots. They never
   *  receive a spec (no phone) and pick answers via the AI tick. */
  ai: boolean;
  /** Probability this bot picks the correct answer this round. */
  aiAccuracy: number;
  /** Game-time (s into the question) at which the bot will lock its
   *  pick. -1 once it has answered or the question hasn't started. */
  aiAnswerAt: number;
  /** Per-bot personality, set on spawn — drives accuracy + timing
   *  distributions so a human plays four distinct opponents not four
   *  copies of the same player. */
  aiPersonality: 'steady' | 'fast' | 'cautious' | 'spiky';
}

/** How many slots the host keeps populated. If fewer humans are paired,
 *  the host fills the rest with AI so the screen is never an empty
 *  lobby — a passer-by always sees a game in progress and scans in. */
export const MIN_AI_FILL = 4;

const AI_NAMES = ['BOT-Δ', 'BOT-Ω', 'BOT-Ψ', 'BOT-Φ', 'BOT-Σ', 'BOT-Λ', 'BOT-Θ', 'BOT-Ξ'];

function pickAiPersonality(): Player['aiPersonality'] {
  const r = Math.random();
  if (r < 0.35) return 'steady';     // medium accuracy, average pace
  if (r < 0.60) return 'fast';       // quick, slightly less accurate
  if (r < 0.85) return 'cautious';   // slow, slightly more accurate
  return 'spiky';                    // wild — high variance
}

/** Compute a bot's accuracy for THIS round, scaling with the round
 *  index so the game gets harder over the 10 rounds. Capped at 0.92
 *  so a human paying attention can still win the speed bonus race. */
function rollAccuracy(personality: Player['aiPersonality'], roundIx: number, total: number): number {
  // Base by personality.
  const base: Record<Player['aiPersonality'], number> = {
    steady: 0.55, fast: 0.50, cautious: 0.65, spiky: 0.50,
  };
  // Scale up with round index (0..total-1). Easy rounds early, hard rounds late.
  const ramp = Math.min(1, roundIx / Math.max(1, total - 1)) * 0.20;
  // Spiky bots have a much wider per-round wobble.
  const wobbleRange = personality === 'spiky' ? 0.30 : 0.12;
  const wobble = (Math.random() - 0.5) * 2 * wobbleRange;
  return Math.max(0.20, Math.min(0.92, base[personality] + ramp + wobble));
}

/** Compute a bot's answer time for THIS round. Fast bots commit
 *  early (sometimes too early), cautious bots take their time. */
function rollAnswerTime(personality: Player['aiPersonality']): number {
  switch (personality) {
    case 'fast':     return 1.0 + Math.random() * 3.5;     // 1.0 - 4.5s
    case 'cautious': return 4.5 + Math.random() * 7.0;     // 4.5 - 11.5s
    case 'spiky':    return Math.random() * 12;            // anywhere
    default:         return 2.0 + Math.random() * 6.0;     // steady: 2 - 8s
  }
}

function currentRoundIndex(world: World): number {
  const ph = world.phase;
  if (ph.kind === 'pre-round' || ph.kind === 'question' || ph.kind === 'reveal') return ph.round.index;
  return 0;
}

export interface RoundView {
  /** Question index inside the shuffled session order (0-based). */
  index: number;
  total: number;
  question: string;
  /** Options as displayed this round (shuffled). */
  options: [string, string, string, string];
  /** Index in the displayed options of the correct answer. */
  correctOption: 0 | 1 | 2 | 3;
}

export type Phase =
  | { kind: 'lobby' }
  | { kind: 'pre-round'; round: RoundView; startedAt: number }
  | { kind: 'question';  round: RoundView; startedAt: number }
  | { kind: 'reveal';    round: RoundView; startedAt: number }
  | { kind: 'final';     startedAt: number };

export interface World {
  players: Map<number, Player>;
  phase: Phase;
  time: number;
  /** Shuffled question IDs for this session (indices into QUESTIONS). */
  deck: number[];
  /** Next deck position to draw. */
  cursor: number;
}

export function newWorld(): World {
  return {
    players: new Map(),
    phase: { kind: 'lobby' },
    time: 0,
    deck: shuffledDeck(),
    cursor: 0,
  };
}

export function addPlayer(world: World, p: number, colour: string, ai = false): Player {
  let player = world.players.get(p);
  if (player) return player;
  const personality = ai ? pickAiPersonality() : 'steady';
  const total = Math.min(ROUNDS_PER_GAME, QUESTIONS.length);
  player = {
    p, colour, score: 0,
    pick: null, pickedAt: null,
    lastCorrect: false, lastDelta: 0,
    ai,
    aiPersonality: personality,
    aiAccuracy: ai ? rollAccuracy(personality, currentRoundIndex(world), total) : 1,
    aiAnswerAt: ai ? rollAnswerTime(personality) : -1,
  };
  world.players.set(p, player);
  return player;
}

/** Top up empty slots in 0..MIN_AI_FILL-1 with AI players. Call on
 *  every step so dropped humans get replaced and the screen never
 *  shows an empty lobby. */
export function autoFillAI(world: World): void {
  if (world.phase.kind === 'final') return;
  const colours = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a', '#ff5fa5', '#5fdbff', '#ffa55f', '#a5ff5f'];
  for (let p = 0; p < MIN_AI_FILL; p++) {
    if (!world.players.has(p)) {
      addPlayer(world, p, colours[p % colours.length]!, true);
    }
  }
}

export function botLabel(p: number): string {
  return AI_NAMES[p % AI_NAMES.length]!;
}

export function removePlayer(world: World, p: number): void {
  world.players.delete(p);
  if (world.players.size === 0 && world.phase.kind !== 'lobby') {
    world.phase = { kind: 'lobby' };
  }
}

export function humanCount(world: World): number {
  let n = 0;
  for (const p of world.players.values()) if (!p.ai) n += 1;
  return n;
}

/** Called when a human pairs + signals ready. Fills bots in, kicks
 *  the first round. No-op if there are no humans yet (cold-boot host
 *  page on its own waits for someone to scan). */
export function tryStart(world: World): void {
  if (world.phase.kind !== 'lobby') return;
  if (humanCount(world) === 0) return;
  autoFillAI(world);
  startRound(world);
}

/** Bring the world back to lobby state. Drops bots so the empty
 *  page reads as "waiting for a player" rather than "demo mode". */
export function returnToLobby(world: World): void {
  for (const [p, player] of [...world.players.entries()]) {
    if (player.ai) world.players.delete(p);
  }
  world.phase = { kind: 'lobby' };
}

/** Slot-keyed input from a phone. Only A/B/X/Y carry meaning here;
 *  joystick frames are ignored if the player sends them (defensive). */
export function applyInput(world: World, p: number, slot: string, value: string): void {
  // Native pads neutralise both sticks when their layout changes. Stick-end
  // frames use value 1; only an actual answer-button press can start a quiz.
  if (!isAnswerPress(slot, value)) return;
  const ix = OPTION_SLOTS.indexOf(slot);
  const player = world.players.get(p);
  if (!player) return;
  // Any human button press in lobby kicks off the first round.
  if (world.phase.kind === 'lobby' && !player.ai) {
    tryStart(world);
    return;
  }
  if (world.phase.kind !== 'question') return;
  // The first answer locks its choice and scoring timestamp for the round.
  if (player.pick !== null) return;
  player.pick = ix as 0 | 1 | 2 | 3;
  player.pickedAt = world.time - world.phase.startedAt;
}

/** Advance the world by dt seconds. Drives the phase transitions. */
export function step(world: World, dt: number): void {
  world.time += dt;
  const phase = world.phase;
  if (phase.kind === 'pre-round') {
    if (world.time - phase.startedAt >= PRE_ROUND_S) {
      world.phase = { kind: 'question', round: phase.round, startedAt: world.time };
      const total = Math.min(ROUNDS_PER_GAME, QUESTIONS.length);
      for (const player of world.players.values()) {
        player.pick = null;
        player.pickedAt = null;
        player.lastDelta = 0;
        player.lastCorrect = false;
        if (player.ai) {
          // Reroll per question — accuracy scales with round index,
          // wobble is per-personality.
          player.aiAccuracy = rollAccuracy(player.aiPersonality, phase.round.index, total);
          player.aiAnswerAt = rollAnswerTime(player.aiPersonality);
        }
      }
    }
  } else if (phase.kind === 'question') {
    const elapsed = world.time - phase.startedAt;
    // Tick AI: each bot locks in its pick at its chosen answer time.
    for (const player of world.players.values()) {
      if (!player.ai || player.pick !== null) continue;
      if (elapsed >= player.aiAnswerAt) {
        const correct = Math.random() < player.aiAccuracy;
        if (correct) {
          player.pick = phase.round.correctOption;
        } else {
          // Pick a wrong option uniformly.
          const wrongs: Array<0 | 1 | 2 | 3> = [];
          for (let i = 0 as 0 | 1 | 2 | 3; i < 4; i = (i + 1) as 0 | 1 | 2 | 3) {
            if (i !== phase.round.correctOption) wrongs.push(i);
          }
          player.pick = wrongs[Math.floor(Math.random() * wrongs.length)]!;
        }
        player.pickedAt = elapsed;
      }
    }
    const everyone = [...world.players.values()].every((p) => p.pick !== null);
    if (everyone || elapsed >= QUESTION_TIME_S) {
      scoreRound(world, phase.round, elapsed);
      world.phase = { kind: 'reveal', round: phase.round, startedAt: world.time };
    }
  } else if (phase.kind === 'reveal') {
    if (world.time - phase.startedAt >= REVEAL_S) {
      if (world.cursor >= world.deck.length || world.cursor >= ROUNDS_PER_GAME) {
        world.phase = { kind: 'final', startedAt: world.time };
      } else {
        startRound(world);
      }
    }
  } else if (phase.kind === 'final') {
    if (world.time - phase.startedAt >= FINAL_HOLD_S) {
      if (humanCount(world) === 0) {
        // Nobody real to play against — drop bots and idle in lobby.
        returnToLobby(world);
        return;
      }
      // Auto-restart while humans are still paired. Reset scores so
      // the existing players are on fresh ground; new joiners get
      // folded in via addPlayer.
      for (const player of world.players.values()) {
        player.score = 0;
        player.pick = null;
        player.pickedAt = null;
        player.lastCorrect = false;
        player.lastDelta = 0;
      }
      world.deck = shuffledDeck();
      world.cursor = 0;
      world.phase = { kind: 'lobby' };
      tryStart(world);
    }
  }
}

function startRound(world: World): void {
  const deckIndex = world.deck[world.cursor];
  if (deckIndex === undefined) {
    world.phase = { kind: 'final', startedAt: world.time };
    return;
  }
  world.cursor += 1;
  const q = QUESTIONS[deckIndex]!;
  const round = makeRoundView(q, world.cursor - 1);
  world.phase = { kind: 'pre-round', round, startedAt: world.time };
}

function scoreRound(world: World, round: RoundView, elapsed: number): void {
  for (const player of world.players.values()) {
    if (player.pick === round.correctOption) {
      const at = player.pickedAt ?? elapsed;
      const speedFrac = Math.max(0, 1 - at / QUESTION_TIME_S);
      const delta = SCORE_BASE + Math.round(SCORE_SPEED_MAX * speedFrac);
      player.score += delta;
      player.lastDelta = delta;
      player.lastCorrect = true;
    } else {
      player.lastDelta = 0;
      player.lastCorrect = false;
    }
  }
}

function makeRoundView(q: Question, index: number): RoundView {
  const order = shuffleN(4);
  const opts = order.map((i) => q.options[i]) as [string, string, string, string];
  const correctOption = order.indexOf(q.correct) as 0 | 1 | 2 | 3;
  return { index, total: Math.min(ROUNDS_PER_GAME, QUESTIONS.length), question: q.q, options: opts, correctOption };
}

function shuffledDeck(): number[] {
  const ids = Array.from({ length: QUESTIONS.length }, (_, i) => i);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
  }
  return ids.slice(0, ROUNDS_PER_GAME);
}

function shuffleN(n: number): number[] {
  const xs = Array.from({ length: n }, (_, i) => i);
  for (let i = xs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [xs[i], xs[j]] = [xs[j]!, xs[i]!];
  }
  return xs;
}

export function leaderboard(world: World): Player[] {
  return [...world.players.values()].sort((a, b) => b.score - a.score);
}

/** Time remaining (s) in the current question phase; null otherwise. */
export function timeLeft(world: World): number | null {
  if (world.phase.kind !== 'question') return null;
  return Math.max(0, QUESTION_TIME_S - (world.time - world.phase.startedAt));
}
